import { Client, GoalCategory, Proposal } from "creso-core/types";
import { Route } from "@/routes/(auth)/_headed/clients/$id/planning";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  VisuallyHidden,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "web-design-system";
import {
  ArrowUpWideNarrowIcon,
  EllipsisVertical,
  ImportIcon,
} from "lucide-react";
import { useCallback, useState } from "react";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { Link, useRouter } from "@tanstack/react-router";
import Amount from "@funds/components/atoms/amount";
import {
  useDeleteProposal,
  useDownloadProposalReport,
  webDownload,
} from "creso-core/hooks";
import { handleError } from "@/utils/error";
import { useQueryClient } from "@tanstack/react-query";
import { ACTIVE_STATUSES, TransactionOrderType } from "creso-core/constants";
import { AmcLogoGroup, GoalIcon } from "web-core";
import { formatAsCrore } from "creso-core/utils";

export function ProposalCard(props: {
  proposal: Proposal;
  client: Client;
  withIcon?: boolean;
}) {
  const navigate = Route.useNavigate();
  const router = useRouter();

  const { withIcon = true, client } = props;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes =
      minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : "";
    return `${day} ${month} ${formattedHours}${formattedMinutes} ${ampm}`;
  };

  const downloadProposal = useDownloadProposalReport();
  // e.g. "Step-up by 10% every 6 months" or "Step-up by ₹1,000 every 12 months"
  const stepUpAmount = Number(props.proposal.step_up_amount) || 0;
  const stepUpPercentage = Number(props.proposal.step_up_percentage) || 0;
  const stepUpLabel =
    props.proposal.step_up_frequency && (stepUpAmount || stepUpPercentage)
      ? `Step-up: ${
          stepUpAmount
            ? formatAsCrore(stepUpAmount, 0)
            : `${parseFloat(stepUpPercentage.toFixed(2))}%`
        } / ${props.proposal.step_up_frequency} months`
      : null;

  const handleDownloadProposal = useCallback(async () => {
    if (!props.proposal.pdf_file_available) {
      navigate({
        to: ".",
        search: {
          modal: "proposal",
          proposal_id: props.proposal.id,
          state: "download",
          client_id: props.proposal.client,
        },
      });
      return;
    }

    try {
      const pdfBlob = await downloadProposal.mutateAsync(props.proposal.id);
      await webDownload(pdfBlob, `${props.proposal.name}.pdf`);
    } catch (e) {
      handleError(e, "Failed to download proposal report");
    }
  }, [downloadProposal, props.proposal, navigate]);

  return (
    <div className="flex rounded-lg border justify-between border-border flex-col bg-background p-4 gap-4 transition-all">
      {/* Header with icon, name, date and menu */}
      <div className="flex flex-row items-start gap-3 border-b border-border pb-4">
        {withIcon && (
          <GoalIcon category={props.proposal.goal_category as GoalCategory} />
        )}
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <span className="font-semibold text-sm truncate">
            {props.proposal.name}
          </span>
          <div className="flex flex-row gap-2 items-center">
            <span className="text-xs text-muted-foreground">
              {formatDate(props.proposal.created_at)}
            </span>
            {props.proposal.status === "DRAFT" && (
              <Badge
                variant="custom"
                className="text-tag-yellow-foreground bg-tag-yellow"
              >
                Draft
              </Badge>
            )}
          </div>
        </div>
        <ConditionalRenderer
          condition={
            !!client?.status &&
            ACTIVE_STATUSES.includes(client.status) &&
            props.proposal.status !== "DRAFT" &&
            props.proposal.schemes_count > 0
          }
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="-mt-1 -mr-2 h-8 w-8"
                onClick={() => {
                  const collectionTarget = [
                    ...(Number(props.proposal.total_lumpsum_amount) > 0
                      ? [TransactionOrderType.PURCHASE]
                      : []),
                    ...(Number(props.proposal.total_sip_amount)
                      ? [TransactionOrderType.XSIP]
                      : []),
                  ];
                  router.navigate({
                    to: ".",
                    search: (prev) => ({
                      ...prev,
                      modal: "transaction",
                      client_id: props.client.id,
                      collection_id: props.proposal.id,
                      collection_type: "proposal",
                      type: collectionTarget[0],
                      collection_target: collectionTarget,
                      action: "create",
                    }),
                  });
                }}
              >
                <ImportIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add funds from proposal to cart</TooltipContent>
          </Tooltip>
        </ConditionalRenderer>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 w-8 -mr-2 -mt-1">
              <EllipsisVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {props.proposal.status !== "DRAFT" && (
              <DropdownMenuItem onClick={handleDownloadProposal}>
                Download
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() =>
                navigate({
                  to: Route.fullPath,
                  search: {
                    client_id: props.client.id,
                    proposal_id: props.proposal.id,
                    modal: "proposal",
                  },
                  replace: true,
                })
              }
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="text-destructive">
              <DeleteProposal
                id={props.proposal.id}
                goal={props.proposal.goal}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* AMC logos and scheme count */}
      <ConditionalRenderer condition={props.proposal.schemes_count > 0}>
        <div className="flex flex-row items-center gap-2">
          <AmcLogoGroup
            hideDisplayCount
            size="sm"
            items={props.proposal.proposal_funds}
          />
          <span className="text-xs text-muted-foreground">
            +{props.proposal.schemes_count} schemes
          </span>
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={props.proposal.schemes_count === 0}>
        <Link
          className="text-sm font-medium"
          to={Route.fullPath}
          params={{ id: client.id.toString() }}
          search={{
            client_id: client.id,
            proposal_id: props.proposal.id,
            state:
              Number(props.proposal.total_lumpsum_amount) > 0
                ? "lumpsum"
                : "sip",
            modal: "proposal",
          }}
        >
          + Add funds
        </Link>
      </ConditionalRenderer>

      {/* Bottom row with One-time and Monthly SIP amounts */}
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">One-time</span>
          <span className="text-sm font-semibold">
            <Amount precision={0}>{props.proposal.total_lumpsum_amount}</Amount>
          </span>
        </div>
        <div className="flex flex-col gap-0.5 text-right">
          <span className="text-xs text-muted-foreground">Monthly SIP</span>
          <span className="flex items-center justify-end gap-1 text-sm font-semibold">
            <Amount precision={0}>{props.proposal.total_sip_amount}</Amount>
            {stepUpLabel ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-0.5 text-muted-foreground text-xxs">
                    <ArrowUpWideNarrowIcon className="size-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{stepUpLabel}</TooltipContent>
              </Tooltip>
            ) : null}
          </span>
        </div>
      </div>
    </div>
  );
}

function DeleteProposal(props: { id: number; goal?: number }) {
  const { mutateAsync: deleteProposal } = useDeleteProposal();

  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const handleDeleteProposal = async () => {
    try {
      await deleteProposal(props.id);
      if (props.goal) {
        queryClient.invalidateQueries({ queryKey: ["goals"] });
        queryClient.invalidateQueries({ queryKey: ["goal-proposals"] });
      }
    } catch (error) {
      handleError(error, "Failed to delete proposal");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        className="hover:bg-muted w-full text-destructive flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0"
        onClick={() => setOpen(true)}
      >
        Delete
      </DialogTrigger>
      <DialogContent>
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Delete Proposal</DialogTitle>
          </DialogHeader>
          <DialogDescription>Are you sure</DialogDescription>
        </VisuallyHidden>
        <span className="text-lg font-semibold">
          Are you sure you want to delete this proposal?
        </span>
        <span className="text-sm text-muted-foreground">
          The proposal will be deleted. This action cannot be undone.
        </span>
        <DialogFooter>
          <Button onClick={handleDeleteProposal} variant="outline">
            Yes, Delete
          </Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
